import { useStore } from "@/lib/store";
import { accountsApi } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Trash2, Plus, AlertCircle } from "lucide-react";

export function SettingsPage() {
  const { user, isPro, notify } = useStore();
  const queryClient = useQueryClient();
  const [disconnecting, setDisconnecting] = useState<string | null>(null);

  if (!user) return null;

  async function handleConnect() {
    try {
      const { authUrl } = await accountsApi.connect();
      window.location.href = authUrl;
    } catch (err: any) {
      notify({
        type: "error",
        message: err.message || "Failed to connect account",
      });
    }
  }

  async function handleDisconnect(id: string) {
    if (!confirm("Disconnect this account? All associated data will be deleted.")) return;
    setDisconnecting(id);
    try {
      await accountsApi.disconnect(id);
      queryClient.invalidateQueries({ queryKey: ["user"] });
    } catch (err: any) {
      notify({
        type: "error",
        message: err.message || "Failed to disconnect",
      });
    } finally {
      setDisconnecting(null);
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight mb-6">Settings</h1>

      {/* Profile */}
      <section className="rounded-xl border border-border bg-white p-5 mb-6">
        <h2 className="text-sm font-medium uppercase tracking-wide text-neutral-400 mb-4">
          Profile
        </h2>
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-full bg-neutral-200 flex items-center justify-center text-lg font-bold">
            {user.username.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-semibold">@{user.username}</p>
            {user.name && <p className="text-sm text-neutral-500">{user.name}</p>}
          </div>
          <div className="ml-auto">
            <span
              className={`rounded-full px-3 py-1 text-xs font-medium uppercase tracking-wide ${
                user.subscriptionStatus === "pro"
                  ? "bg-black text-white"
                  : "bg-neutral-100 text-neutral-500"
              }`}
            >
              {user.subscriptionStatus}
            </span>
          </div>
        </div>
      </section>

      {/* Connected Accounts */}
      <section className="rounded-xl border border-border bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-medium uppercase tracking-wide text-neutral-400">
            Connected Accounts
          </h2>
          {isPro() && (
            <button
              onClick={handleConnect}
              className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-surface-hover transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Account
            </button>
          )}
        </div>

        <div className="space-y-3">
          {user.accounts.map((account) => (
            <div
              key={account.id}
              className="flex items-center gap-3 rounded-lg border border-border p-3"
            >
              <div className="h-8 w-8 rounded-full bg-neutral-200 flex items-center justify-center text-sm font-semibold">
                {account.username.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">@{account.username}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {account.isPrimary && (
                    <span className="text-[10px] uppercase tracking-wide text-neutral-400">
                      Primary
                    </span>
                  )}
                  <span
                    className={`text-[10px] uppercase tracking-wide ${
                      account.syncEnabled ? "text-success" : "text-neutral-400"
                    }`}
                  >
                    {account.syncEnabled ? "Syncing" : "Paused"}
                  </span>
                  {account.lastSyncedAt && (
                    <span className="text-[10px] text-neutral-400">
                      Last sync: {new Date(account.lastSyncedAt * 1000).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
              {!account.isPrimary && (
                <button
                  onClick={() => handleDisconnect(account.id)}
                  disabled={disconnecting === account.id}
                  className="rounded-lg p-2 text-neutral-400 hover:text-danger hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        {!isPro() && user.accounts.length >= 1 && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-surface p-3">
            <AlertCircle className="h-4 w-4 text-neutral-400 mt-0.5 shrink-0" />
            <p className="text-xs text-neutral-500">
              Upgrade to Pro to connect up to 5 Threads accounts.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
